/**
 * Staff surface (`/s/*`, 08 §5). Session cookie `becca_staff` (httpOnly, SameSite=Lax), CSRF header
 * `x-becca-csrf` equal to the `becca_csrf` cookie on every non-GET. Viewers are read-only. Every content
 * write records an `audit` row. Everything is scoped to the session's active venue; no body or query
 * names a venue except `POST /s/venue/switch`.
 */
import { z } from 'zod';
import { Currency, DateString, Email, HoursByWeekday, Id, IdParams, Instant, listOf, PhoneE164, Slug, } from '../common.js';
import { Addon, AddonInput, AgreementTemplate, AgreementTemplateInput, Closure, ClosureInput, ClosureRule, ClosureRuleInput, EventType, EventTypeInput, KnowledgeArticle, KnowledgeArticleInput, Package, PackageInput, PricingRule, PricingRuleInput, Room, RoomInput, DEFAULT_SETTINGS, Settings, SettingsValues, SpaceProduct, SpaceProductInput, Template, TemplateInput, Venue, VenueInput, VoiceGuide, VoiceGuideInput, } from '../content.js';
import { BookingSummary } from '../bookings.js';
import { ExtendHoldBody, Hold } from '../holds.js';
import { Consent, Contact, Lead, LeadSource, ManualStage, Message, PreferredChannel, Qualification, QuestionStatus, Stage, StaffNote, StaffNoteKind, StaffQuestion, TimelineEntry, } from '../leads.js';
import { PlanInput, Quote } from '../quote.js';
import { AuditEntry, StaffAccount, StaffRole, StaffUser, StaffUserInput, VenueAccess } from '../staff.js';
import { CalendarTour, LeadToursBadge } from '../tours.js';
// ── Auth ──────────────────────────────────────────────────────────────────
export const AuthRequestBody = z.object({ email: Email });
export const VerifyQuery = z.object({ token: z.string().min(16).max(200) });
/** The signed-in account, every venue it can reach, and the session's active venue. */
export const Me = z.object({
    user: StaffAccount,
    venues: z.array(VenueAccess),
    activeVenueId: Id,
    /** The active venue: every date and time the console shows is in its `timezone`. */
    activeVenue: z.object({
        id: Id,
        slug: Slug,
        name: z.string(),
        timezone: z.string(),
        currency: Currency,
    }),
    /** Effective role at the active venue (`owner` for helix_admin without a membership). */
    activeRole: StaffRole,
});
/** `POST /s/venue/switch` — only to a venue listed in `Me.venues`. */
export const SwitchVenueBody = z.object({ venueId: Id });
/**
 * `GET /s/venue/members`: the active venue's active members, for the assignee pickers and filters (every
 * role may read it; `/s/staff-users` stays owner-only). Owners and coordinators can hold leads.
 */
export const VenueMember = z.object({ id: Id, name: z.string(), role: StaffRole });
/** The roles a lead can be assigned to (a viewer reads, so never holds a lead). */
export const ASSIGNABLE_ROLES = ['owner', 'coordinator'];
// ── Settings ──────────────────────────────────────────────────────────────
/**
 * Who a lead goes to when nobody has it yet (Ivan, 2026-10-29). Each rule, when on, assigns an unassigned
 * lead to the staff member who acted; none ever moves a lead someone already has.
 */
export const LeadAssignmentRules = z.object({
    /** Whoever adds a lead by hand (`POST /s/leads`) gets it. */
    onNewLead: z.boolean(),
    /** Whoever saves a lead's plan for the guest (`PATCH /s/leads/:id/plan`) gets it. */
    onPlanBuilt: z.boolean(),
    /** Whoever takes the conversation over from the concierge gets it. */
    onTakeOver: z.boolean(),
});
export const DEFAULT_LEAD_ASSIGNMENT = {
    onNewLead: true,
    onPlanBuilt: true,
    onTakeOver: true,
};
/**
 * The venue's settings (`settings.values`) as staff read and write them (`/s/settings`): the shared
 * `SettingsValues` plus the staff-only keys. Those live here rather than in content.ts, whose schemas
 * the guest embed carries (its 150 KB budget is full). A row written before `0019_lead_assignment` (no
 * `leadAssignment`) reads as every rule on.
 */
export const StaffSettingsValues = SettingsValues.extend({
    leadAssignment: LeadAssignmentRules.default(DEFAULT_LEAD_ASSIGNMENT),
});
export const StaffSettings = Settings.extend({ values: StaffSettingsValues });
export const DEFAULT_STAFF_SETTINGS = {
    ...DEFAULT_SETTINGS,
    leadAssignment: DEFAULT_LEAD_ASSIGNMENT,
};
// ── Leads ─────────────────────────────────────────────────────────────────
/**
 * Why a lead is assigned to the staff member it is (Ivan, 2026-10-29: commissions will follow who owned
 * the lead): `claimed` (they took it), `assigned` (an owner gave it to them), or a rule in
 * `StaffSettingsValues.leadAssignment` — `new_lead` (they added it by hand), `plan_built` (they saved its
 * plan), `took_over` (they took the conversation over) — or `imported` from Perfect Venue.
 */
export const AssignedReason = z.enum([
    'claimed',
    'assigned',
    'new_lead',
    'plan_built',
    'took_over',
    'imported',
]);
/**
 * A lead as the staff routes return it: `Lead` plus who it is assigned to (claimed, assigned, or by a
 * rule), when and why; commissions will follow it. Not `ownerStaffId`, which only means a person took the
 * conversation over from the concierge: assignment never pauses the concierge. Kept out of `Lead`
 * (leads.ts), which the guest embed carries. All nullish, so an older API still parses.
 */
export const StaffLead = Lead.extend({
    assignedStaffId: Id.nullish(),
    assignedAt: Instant.nullish(),
    assignedReason: AssignedReason.nullish(),
});
/** The guest web sources (the plan page, wherever it was linked from): the source filter's `web`. */
export const WEB_LEAD_SOURCES = [
    'website',
    'standalone',
    'link',
    'qr',
    'instagram',
    'referral',
];
/** The leads list's source filter: one source, or `web` for every guest web source. */
export const LeadSourceFilter = z.union([LeadSource, z.literal('web')]);
/** The sources a filter value matches. */
export function leadSourcesOf(filter) {
    return filter === 'web' ? [...WEB_LEAD_SOURCES] : [filter];
}
/** The assignee filter: `me` (the signed-in staff member), `none` (unassigned), or a staff user's id. */
export const LeadAssignedFilter = z.union([z.enum(['me', 'none']), Id]);
export const LeadListQuery = z.object({
    stage: Stage.optional(),
    /** Where the lead came from (phase 3: find imported leads with `perfect_venue`). */
    source: LeadSourceFilter.optional(),
    /** Who the lead is assigned to (`Lead.assignedStaffId`). */
    assigned: LeadAssignedFilter.optional(),
    /** Matches contact name, email, phone, event name. */
    q: z.string().trim().max(200).optional(),
    /** Event date range. */
    from: DateString.optional(),
    to: DateString.optional(),
    /** Page size; the API defaults to 50. */
    limit: z.coerce.number().int().min(1).max(500).optional(),
    /** Rows to skip (newest activity first). */
    offset: z.coerce.number().int().min(0).max(1_000_000).optional(),
});
export const LeadListItem = z.object({
    id: Id,
    stage: Stage,
    /** Set while the stage still rests on a Perfect Venue import (`Lead.importedStage`). */
    importedStage: Stage.nullish(),
    /** Set while the stage is one staff set by hand (`Lead.manualStage`). Absent from older APIs. */
    manualStage: ManualStage.nullish(),
    manualStageSetAt: Instant.nullish(),
    source: LeadSource,
    contactName: z.string().nullable(),
    /** E.164, for the console's Call button. */
    contactPhone: z.string().nullable(),
    eventTypeLabel: z.string().nullable(),
    eventName: z.string().nullable(),
    date: DateString.nullable(),
    guestCount: z.number().int().nullable(),
    productName: z.string().nullable(),
    activeHoldId: Id.nullable(),
    activeHoldExpiresAt: Instant.nullable(),
    openQuestions: z.number().int(),
    lastActivityAt: Instant,
    createdAt: Instant,
    /** Title fallback when there is neither an event name nor a contact name. Staff-only, never logged. */
    contactEmail: z.string().nullable(),
    /** The space of the booking, else of the latest hold, else of the plan. */
    spaceName: z.string().nullable(),
    /** The event's start and end, from the latest active or converted hold, else the plan's date + time. */
    eventStartsAt: Instant.nullable(),
    eventEndsAt: Instant.nullable(),
    /** `totalCents` of the latest quote. */
    quoteTotalCents: z.number().int().nullable(),
    /** The booking's totals snapshot `totalCents`, when a non-cancelled booking exists. */
    bookingTotalCents: z.number().int().nullable(),
    /** Last message that reached the guest (web, email, SMS; sent or delivered) or answered call. */
    lastContactedAt: Instant.nullable(),
    /** Phase 4: when code marked the lead Qualified (03 §4.5). Absent from older APIs. */
    qualifiedAt: Instant.nullish(),
    /** Phase 4: the tours badge (a booked tour or call is a badge, not a stage). Absent from older APIs. */
    tours: LeadToursBadge.nullish(),
    /** Who the lead is assigned to (`Lead.assignedStaffId`) and their name. Absent from older APIs. */
    assignedStaffId: Id.nullish(),
    assignedStaffName: z.string().nullish(),
});
/** `total` counts every match before `limit`/`offset`. */
export const LeadList = z.object({ items: z.array(LeadListItem), total: z.number().int() });
/**
 * `GET /s/leads/pipeline`: the list's search, source, assignee and event-date filters; stage and paging do
 * not apply.
 */
export const LeadPipelineQuery = LeadListQuery.pick({
    q: true,
    from: true,
    to: true,
    source: true,
    assigned: true,
});
/** A count of leads and the sum of their value: booking total, else latest quote total, else 0. */
export const PipelineBucket = z.object({ count: z.number().int(), valueCents: z.number().int() });
/**
 * Counts and value per stage at the active venue. `stages` holds every `Stage` in funnel order, zeros
 * included; `active` sums every stage before `completed` (not `lost`); `completedYtd` is `completed`
 * leads whose event date falls in the venue-local calendar year up to today.
 */
export const LeadPipeline = z.object({
    active: PipelineBucket,
    stages: z.array(PipelineBucket.extend({ stage: Stage })),
    completedYtd: PipelineBucket,
});
export const LeadDetail = z.object({
    lead: StaffLead,
    contact: Contact.nullable(),
    consents: z.array(Consent),
    holds: z.array(Hold),
    quotes: z.array(Quote),
    messages: z.array(Message),
    notes: z.array(StaffNote),
    questions: z.array(StaffQuestion),
    timeline: z.array(TimelineEntry),
    /** Phase 4: the tours badge (the full list is `GET /s/leads/:id/tours`). Absent from older APIs. */
    tours: LeadToursBadge.nullish(),
    /** Phase 4: which Qualified conditions hold, for the badge's "why". Absent from older APIs. */
    qualification: Qualification.nullish(),
    /**
     * The stage the lead's records give without the hand-set one (what clearing `manualStage` restores); the
     * console offers `manualStageOptions(derivedStage)`. Absent from older APIs.
     */
    derivedStage: Stage.nullish(),
    /** The name of whoever set `lead.manualStage` ("set by Daniel, 2 h ago"). */
    manualStageSetByName: z.string().nullish(),
    /** The name of the staff member who put the plan together (`lead.planSetBy`); null once the guest changed it. */
    planSetByName: z.string().nullish(),
    /** The name of the staff member the lead is assigned to (`lead.assignedStaffId`). Absent from older APIs. */
    assignedStaffName: z.string().nullish(),
});
// ── Lead assignment (Ivan, 2026-10-29) ────────────────────────────────────
/**
 * `PATCH /s/leads/:id/assignment`: set, change or clear (null) who the lead is assigned to. The target
 * must be an active owner or coordinator of this venue (409 NOT_A_MEMBER). Who may do what is
 * `assignmentRefusal`.
 */
export const AssignLeadBody = z.object({ staffUserId: Id.nullable() }).strict();
/**
 * Why `actor` may not move a lead assigned to `current` (a staff user id, or null when unassigned) to
 * `target` (null releases it), or null when they may. Owners assign anyone; a coordinator may take an
 * unassigned lead for themselves or release one assigned to them; viewers read. The API, the lead page's
 * control and the fake share this rule; the target's membership is the API's to check.
 */
export function assignmentRefusal(actor, current, target) {
    if (actor.role === 'viewer')
        return 'Viewers cannot assign leads.';
    if (actor.role === 'owner' || current === target)
        return null;
    if (target === actor.id && current === null)
        return null;
    if (target === null && current === actor.id)
        return null;
    return current !== null && current !== actor.id
        ? 'This lead is assigned to someone else; only an owner can move it.'
        : 'Only an owner can assign a lead to someone else.';
}
export const NoteBody = z.object({
    body: z.string().trim().min(1).max(10_000),
    kind: StaffNoteKind.default('note'),
});
export const LostBody = z.object({ reason: z.string().trim().min(1).max(500) });
/**
 * `PATCH /s/leads/:id`: what staff may change on the lead itself. `sms` needs a mobile on the contact.
 * `stage` sets the stage by hand under `manualStageRefusal` (null clears it and restores the derivation);
 * `lost` needs `lostReason` and closes the lead as Mark lost does; another stage on a lost lead reopens it.
 */
export const UpdateLeadBody = z
    .object({
    preferredChannel: PreferredChannel.nullable().optional(),
    stage: ManualStage.nullable().optional(),
    lostReason: z.string().trim().min(1).max(500).optional(),
})
    .strict();
// ── Staff-built proposals (Daniel, 2026-10-28) ────────────────────────────
/**
 * `PATCH /s/leads/:id/plan`: the plan a coordinator put together for the guest (after a call or an
 * email), in the shape the guest's own `PATCH /g/plan` sends: an omitted field is unchanged, `null`
 * clears it. Refused (409 CONFLICT) on a booked lead. Sets `lead.planSetBy`/`planSetAt`.
 */
export const StaffPlanBody = PlanInput.strict();
export const ProposalChannel = z.enum(['email', 'sms']);
/** `POST /s/leads/:id/send-plan-link`. No channel: the lead's preferred one, else email. */
export const SendPlanLinkBody = z.object({ channel: ProposalChannel.optional() }).strict();
export const SendPlanLinkResponse = z.object({
    channel: ProposalChannel,
    /** The content row it used: `proposal_link` / `proposal_link_sms` when staff set the plan, else `plan_link` / `plan_link_sms`. */
    templateKey: z.string(),
    messageId: Id,
    /** When quiet hours, Shabbat or the texting window hold it: when it goes. Null: it goes now. */
    scheduledAt: Instant.nullable(),
});
/**
 * `POST /s/leads`: a lead staff take by phone or email (source `staff`). An email or a mobile number is
 * required (a phone-only guest gets the placeholder address texts use until they give one). Nothing is
 * sent: staff build the plan and send it with `send-plan-link`. No SMS consent is recorded here; texts
 * need the guest's own consent from their plan.
 */
export const CreateStaffLeadBody = z
    .object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().max(80).nullish(),
    email: Email.nullish(),
    phone: PhoneE164.nullish(),
    eventTypeId: Id.nullish(),
    /** The occasion in the guest's words, e.g. "Maya's 40th". */
    eventName: z.string().trim().max(200).nullish(),
    guestCount: z.number().int().min(1).max(2000).nullish(),
    date: DateString.nullish(),
    dateFlexible: z.boolean().optional(),
    dateFlexibleText: z.string().trim().max(200).nullish(),
    preferredChannel: PreferredChannel.nullish(),
})
    .strict()
    .refine((b) => Boolean(b.email || b.phone), {
    message: 'Add an email or a mobile number',
    path: ['email'],
})
    .refine((b) => b.preferredChannel !== 'sms' || Boolean(b.phone), {
    message: 'Add a mobile number to be contacted by text',
    path: ['phone'],
});
// ── Calendar, questions, audit, embed, uploads ────────────────────────────
export const CalendarQuery = z.object({ from: DateString, to: DateString });
/**
 * The event's own start and end (`window` is the room-taken span, setup to teardown). Always set by the
 * API for holds; null for a booking with no start time on record. Optional on the wire so older
 * readers and fakes still parse.
 */
const EventTimes = {
    eventStartsAt: Instant.nullable().optional(),
    eventEndsAt: Instant.nullable().optional(),
};
/**
 * Rooms × days: the console lays out `rooms` as rows and dates as columns, placing each hold and
 * booking by `roomIds` + `date` and each closure by `productId` (null = every room) + its interval.
 */
export const CalendarResponse = z.object({
    from: DateString,
    to: DateString,
    timezone: z.string(),
    rooms: z.array(Room.pick({ id: true, name: true, slug: true, sort: true, active: true })),
    /** `hours`: the product's bookable hours per weekday (venue time); set by the API, optional on the wire. */
    products: z.array(SpaceProduct.pick({ id: true, name: true, roomIds: true }).extend({ hours: HoursByWeekday.optional() })),
    holds: z.array(Hold.extend({ leadLabel: z.string(), ...EventTimes })),
    bookings: z.array(BookingSummary.extend({ leadLabel: z.string(), ...EventTimes })),
    closures: z.array(Closure),
    /** Phase 4: tours and calls in the range, a layer of their own (not tied to rooms). Absent from older APIs. */
    tours: z.array(CalendarTour).optional(),
});
export const QuestionListQuery = z.object({ status: QuestionStatus.optional() });
export const QuestionListItem = StaffQuestion.extend({ leadLabel: z.string() });
export const AnswerQuestionBody = z.object({
    answer: z.string().trim().min(1).max(10_000),
    /** Send the `question_answered` template to the guest. */
    emailGuest: z.boolean(),
});
export const AuditQuery = z.object({
    entity: z.string().max(80).optional(),
    id: Id.optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
export const EmbedSnippet = z.object({
    /** Origin embed.js is served from. */
    origin: z.string(),
    /** `[hosted_events]` for the WordPress plugin (Hosted Events; `[becca_events]` is its deprecated alias). */
    shortcode: z.string(),
    /** The raw mount div + script tag. */
    html: z.string(),
});
/** `POST /s/uploads` multipart field `file`; images are resized client-side; max 10 MB. */
export const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const UPLOAD_CONTENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/avif',
    'application/pdf',
];
export const UploadResult = z.object({
    key: z.string(),
    url: z.string(),
    contentType: z.string(),
    size: z.number().int(),
});
/** A key returned by `POST /s/uploads` (`<uuid>.<ext>`). */
export const UploadKeyParams = z.object({
    key: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,100}(\.[a-z0-9]{1,8})?$/, 'Not a file key'),
});
export const ClosureListQuery = z.object({ from: DateString.optional(), to: DateString.optional() });
// ── Route table ───────────────────────────────────────────────────────────
/**
 * List / get / create / update (PATCH) / delete for one content collection. Exported for the later
 * phases' tables (phase 4 tour types, availability, time off). The PATCH body must not carry defaults:
 * zod 4 applies them inside `.partial()`, so a partial of an input with defaults resets those fields.
 */
export function crud(path, entity, create, update, roles) {
    const item = `${path}/:id`;
    return {
        list: { method: 'GET', path, response: listOf(entity), status: 200, auth: 'staff', roles },
        get: { method: 'GET', path: item, params: IdParams, response: entity, status: 200, auth: 'staff', roles },
        create: { method: 'POST', path, body: create, response: entity, status: 201, auth: 'staff', roles },
        update: {
            method: 'PATCH',
            path: item,
            params: IdParams,
            body: update,
            response: entity,
            status: 200,
            auth: 'staff',
            roles,
        },
        /** Fails with 409 CONFLICT while other rows reference it: deactivate instead. */
        delete: {
            method: 'DELETE',
            path: item,
            params: IdParams,
            response: null,
            status: 204,
            auth: 'staff',
            roles,
        },
    };
}
/** GET + PUT for a per-venue singleton. */
function singleton(path, entity, input) {
    return {
        get: { method: 'GET', path, response: entity, status: 200, auth: 'staff' },
        put: { method: 'PUT', path, body: input, response: entity, status: 200, auth: 'staff' },
    };
}
/** Versioned rows: GET current, GET history, POST creates the next version. */
function versioned(path, entity, input) {
    return {
        current: { method: 'GET', path: `${path}/current`, response: entity, status: 200, auth: 'staff' },
        history: { method: 'GET', path, response: listOf(entity), status: 200, auth: 'staff' },
        create: { method: 'POST', path, body: input, response: entity, status: 201, auth: 'staff' },
    };
}
export const staffRoutes = {
    auth: {
        /** Emails a sign-in link (`staff_sign_in`) if the address is an active staff user. Always 204. */
        request: {
            method: 'POST',
            path: '/s/auth/request',
            body: AuthRequestBody,
            response: null,
            status: 204,
            auth: 'public',
        },
        /** Sets `becca_staff` + `becca_csrf` and redirects to the console. */
        verify: {
            method: 'GET',
            path: '/s/auth/verify',
            query: VerifyQuery,
            response: null,
            status: 302,
            auth: 'public',
        },
        logout: { method: 'POST', path: '/s/auth/logout', response: null, status: 204, auth: 'staff' },
        me: { method: 'GET', path: '/s/me', response: Me, status: 200, auth: 'staff' },
    },
    venue: {
        ...singleton('/s/venue', Venue, VenueInput),
        /** Active owners, coordinators and viewers of the active venue, for the assignee pickers. */
        members: {
            method: 'GET',
            path: '/s/venue/members',
            response: listOf(VenueMember),
            status: 200,
            auth: 'staff',
        },
        /** Every role may switch; the target must be one of `Me.venues`. */
        switch: {
            method: 'POST',
            path: '/s/venue/switch',
            body: SwitchVenueBody,
            response: Me,
            status: 200,
            auth: 'staff',
            roles: ['owner', 'coordinator', 'viewer'],
        },
    },
    rooms: crud('/s/rooms', Room, RoomInput, RoomInput.partial()),
    spaceProducts: crud('/s/space-products', SpaceProduct, SpaceProductInput, SpaceProductInput.partial()),
    packages: crud('/s/packages', Package, PackageInput, PackageInput.partial()),
    addons: crud('/s/addons', Addon, AddonInput, AddonInput.partial()),
    eventTypes: crud('/s/event-types', EventType, EventTypeInput, EventTypeInput.partial()),
    knowledge: crud('/s/knowledge', KnowledgeArticle, KnowledgeArticleInput, KnowledgeArticleInput.partial()),
    templates: crud('/s/templates', Template, TemplateInput, TemplateInput.partial()),
    /** A rule is a discriminated union, so an update replaces it whole. */
    closureRules: crud('/s/closure-rules', ClosureRule, ClosureRuleInput, ClosureRuleInput),
    closures: {
        ...crud('/s/closures', Closure, ClosureInput, ClosureInput.partial()),
        list: {
            method: 'GET',
            path: '/s/closures',
            query: ClosureListQuery,
            response: listOf(Closure),
            status: 200,
            auth: 'staff',
        },
    },
    agreementTemplates: versioned('/s/agreement-templates', AgreementTemplate, AgreementTemplateInput),
    pricingRules: versioned('/s/pricing-rules', PricingRule, PricingRuleInput),
    voiceGuide: singleton('/s/voice-guide', VoiceGuide, VoiceGuideInput),
    settings: singleton('/s/settings', StaffSettings, StaffSettingsValues),
    staffUsers: crud('/s/staff-users', StaffUser, StaffUserInput, StaffUserInput.partial(), ['owner']),
    uploads: {
        create: {
            method: 'POST',
            path: '/s/uploads',
            body: 'multipart',
            response: UploadResult,
            status: 201,
            auth: 'staff',
        },
        /** Only a file uploaded at the active venue (404 otherwise); idempotent. Content still pointing at it keeps a dead URL. */
        delete: {
            method: 'DELETE',
            path: '/s/uploads/:key',
            params: UploadKeyParams,
            response: null,
            status: 204,
            auth: 'staff',
        },
    },
    leads: {
        list: {
            method: 'GET',
            path: '/s/leads',
            query: LeadListQuery,
            response: LeadList,
            status: 200,
            auth: 'staff',
        },
        /** A lead taken by phone or email (source `staff`); nothing is sent. */
        create: {
            method: 'POST',
            path: '/s/leads',
            body: CreateStaffLeadBody,
            response: StaffLead,
            status: 201,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
        /** Before `get`, so `/s/leads/pipeline` is never read as a lead id. */
        pipeline: {
            method: 'GET',
            path: '/s/leads/pipeline',
            query: LeadPipelineQuery,
            response: LeadPipeline,
            status: 200,
            auth: 'staff',
        },
        get: {
            method: 'GET',
            path: '/s/leads/:id',
            params: IdParams,
            response: LeadDetail,
            status: 200,
            auth: 'staff',
        },
        update: {
            method: 'PATCH',
            path: '/s/leads/:id',
            params: IdParams,
            body: UpdateLeadBody,
            response: StaffLead,
            status: 200,
            auth: 'staff',
        },
        addNote: {
            method: 'POST',
            path: '/s/leads/:id/notes',
            params: IdParams,
            body: NoteBody,
            response: StaffNote,
            status: 201,
            auth: 'staff',
        },
        markLost: {
            method: 'POST',
            path: '/s/leads/:id/lost',
            params: IdParams,
            body: LostBody,
            response: StaffLead,
            status: 200,
            auth: 'staff',
        },
        reopen: {
            method: 'POST',
            path: '/s/leads/:id/reopen',
            params: IdParams,
            response: StaffLead,
            status: 200,
            auth: 'staff',
        },
        /** The plan a coordinator built for the guest; 409 CONFLICT once booked. */
        setPlan: {
            method: 'PATCH',
            path: '/s/leads/:id/plan',
            params: IdParams,
            body: StaffPlanBody,
            response: StaffLead,
            status: 200,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
        /** The caller takes an unassigned lead (409 ALREADY_ASSIGNED, with the holder's name, when someone has it). */
        claim: {
            method: 'POST',
            path: '/s/leads/:id/claim',
            params: IdParams,
            response: StaffLead,
            status: 200,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
        /** Set, change or clear who the lead is assigned to, under `assignmentRefusal` (403 when refused). */
        assign: {
            method: 'PATCH',
            path: '/s/leads/:id/assignment',
            params: IdParams,
            body: AssignLeadBody,
            response: StaffLead,
            status: 200,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
        /** Prices the lead's plan as the guest's quote does and saves it (unsaved once booked). */
        quote: {
            method: 'POST',
            path: '/s/leads/:id/quote',
            params: IdParams,
            response: Quote,
            status: 200,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
        /**
         * Holds the plan's space, date and time (409 PLAN_INCOMPLETE without them; 409 HOLD_CONFLICT /
         * HOLD_LIMIT / NOT_AVAILABLE as the guest gets them).
         */
        hold: {
            method: 'POST',
            path: '/s/leads/:id/holds',
            params: IdParams,
            response: Hold,
            status: 201,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
        /**
         * Sends the guest their plan link now (quiet hours, Shabbat and the texting window still hold it).
         * Email needs a real address (409 CONTACT_REQUIRED); text needs a mobile (409 NO_ADDRESS) and the
         * guest's granted SMS consent (409 CONSENT); a revoked channel is 409 REVOKED.
         */
        sendPlanLink: {
            method: 'POST',
            path: '/s/leads/:id/send-plan-link',
            params: IdParams,
            body: SendPlanLinkBody,
            response: SendPlanLinkResponse,
            status: 201,
            auth: 'staff',
            roles: ['owner', 'coordinator'],
        },
    },
    holds: {
        extend: {
            method: 'POST',
            path: '/s/holds/:id/extend',
            params: IdParams,
            body: ExtendHoldBody,
            response: Hold,
            status: 200,
            auth: 'staff',
        },
        release: {
            method: 'POST',
            path: '/s/holds/:id/release',
            params: IdParams,
            response: Hold,
            status: 200,
            auth: 'staff',
        },
    },
    calendar: {
        get: {
            method: 'GET',
            path: '/s/calendar',
            query: CalendarQuery,
            response: CalendarResponse,
            status: 200,
            auth: 'staff',
        },
    },
    questions: {
        list: {
            method: 'GET',
            path: '/s/questions',
            query: QuestionListQuery,
            response: listOf(QuestionListItem),
            status: 200,
            auth: 'staff',
        },
        answer: {
            method: 'POST',
            path: '/s/questions/:id/answer',
            params: IdParams,
            body: AnswerQuestionBody,
            response: StaffQuestion,
            status: 200,
            auth: 'staff',
        },
    },
    audit: {
        list: {
            method: 'GET',
            path: '/s/audit',
            query: AuditQuery,
            response: listOf(AuditEntry),
            status: 200,
            auth: 'staff',
        },
    },
    embed: {
        snippet: { method: 'GET', path: '/s/embed-snippet', response: EmbedSnippet, status: 200, auth: 'staff' },
    },
};
/** Every staff route as `['rooms.list', def]` pairs — for fakes, tests and route-coverage checks. */
export function flattenStaffRoutes() {
    return Object.entries(staffRoutes).flatMap(([group, routes]) => Object.entries(routes).map(([name, def]) => [
        `${group}.${name}`,
        def,
    ]));
}
//# sourceMappingURL=staff.js.map